import { useEffect, useState } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { HL_WALLET, HL_URL } from '../hyperliquid';
import { tradeResult } from '../tradePnl';
import './TradeDetailPanel.css';

function fmt(price) {
  if (price == null || price === 0) return '—';
  if (price >= 10000) return `$${Number(price).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (price >= 1000)  return `$${Number(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (price >= 1)     return `$${Number(price).toFixed(4)}`;
  return `$${Number(price).toPrecision(4)}`;
}

function fmtTime(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function fmtDuration(ms) {
  if (!ms || ms <= 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function toMs(ts) {
  if (!ts) return null;
  return ts.toDate ? ts.toDate().getTime() : new Date(ts).getTime();
}

export default function TradeDetailPanel({ signal, nextSignalMs }) {
  const coin      = (signal.symbol || '').replace(/USDT$/, '').replace(/PERP$/, '');
  const direction = (signal.direction || '').toUpperCase();
  const signalMs  = toMs(signal.timestamp);

  const [trailEvents,   setTrailEvents]   = useState([]);
  const [fills,         setFills]         = useState([]);
  const [hlFills,       setHlFills]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [trailExpanded, setTrailExpanded] = useState(false);

  useEffect(() => {
    // Duplicate signals were never executed — skip all data fetching
    if (signal.status === 'duplicate_skipped') {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      try {
        // Fetch all trail_events ordered by time, filter in JS (avoids composite index)
        const trailSnap = await getDocs(
          query(collection(db, 'trail_events'), orderBy('timestamp', 'asc'))
        );
        const allEvents = trailSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(e => {
            if ((e.symbol || '') !== coin)                    return false;
            if ((e.direction || '') !== direction)            return false;
            const et = toMs(e.timestamp);
            if (signalMs && et && et < signalMs)              return false;
            if (nextSignalMs && et && et >= nextSignalMs)     return false;
            return true;
          });
        // Hide false-positive "position_closed_externally" events that aren't last
        // (API hiccup caused the trail to think position closed, but it continued)
        const events = allEvents.filter((e, i) => {
          if ((e.note || '') === 'position_closed_externally' && i < allEvents.length - 1) return false;
          return true;
        });

        // Fetch fills from Hyperliquid
        const resp = await fetch(HL_URL, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ type: 'userFills', user: HL_WALLET }),
        });
        const allFills = await resp.json();
        if (!cancelled) setHlFills(Array.isArray(allFills) ? allFills : []);
        // Direction label on HL fills: "Open Long", "Close Long", "Open Short", "Close Short"
        const dirLabel = direction === 'LONG' ? 'Long' : 'Short';
        const coinFills = (Array.isArray(allFills) ? allFills : [])
          .filter(f => {
            if (f.coin !== coin)                          return false;
            if (!(f.dir || '').includes(dirLabel))        return false;
            if (signalMs && f.time < signalMs)            return false;
            if (nextSignalMs && f.time >= nextSignalMs)   return false;
            return true;
          })
          .sort((a, b) => a.time - b.time);

        if (!cancelled) {
          setTrailEvents(events);
          setFills(coinFills);
          setLoading(false);
        }
      } catch (e) {
        console.error('[TradeDetail] load error:', e);
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [coin, direction, signalMs]);

  // ── Derived values ─────────────────────────────────────────────────────────
  const closingFills = fills.filter(f => f.dir?.includes('Close'));
  const hlTrade      = tradeResult(signal, hlFills, nextSignalMs);
  const totalFees    = hlTrade
    ? hlTrade.fees
    : fills.reduce((s, f) => s + parseFloat(f.fee || 0), 0);

  const lastClose = closingFills[closingFills.length - 1];
  // Prefer the backend's real, verified exit_price/exit_time (written directly onto
  // the signal doc as of the Aug 1 fix) over re-deriving from live HL fills. The
  // live-fill window match structurally misses flip-closes: the new signal's own
  // Firestore doc is written *before* close_opposite_position() actually closes the
  // old position on Hyperliquid, so the real closing fill's timestamp lands after
  // nextSignalMs (the window's own upper bound) and never matches.
  const exitPx = signal.exit_price != null ? parseFloat(signal.exit_price)
               : lastClose ? parseFloat(lastClose.px) : null;
  const exitMs = signal.exit_time ? toMs(signal.exit_time)
               : lastClose ? lastClose.time : null;
  const duration  = signalMs && exitMs ? fmtDuration(exitMs - signalMs) : null;

  const estPnl = hlTrade ? hlTrade.net : null;
  const pnlColor = estPnl != null ? (estPnl >= 0 ? 'var(--green)' : 'var(--red)') : 'var(--text-muted)';

  const partialEvent       = trailEvents.find(e => (e.note || '').includes('partial'));
  const closedAfterPartial = trailEvents.some(e => (e.note || '') === 'position_closed_after_partial');
  // Only trust "closed externally" from trail if exchange fills also confirm the position is gone
  const closedExternally   = closingFills.length > 0 && trailEvents.some(e => (e.note || '') === 'position_closed_externally');
  const trailDetectedClose = closedAfterPartial || closedExternally;

  // Human-readable labels for the backend's real exit_reason values (Aug 1 fix)
  const EXIT_REASON_LABELS = {
    sl_hit: 'SL hit',
    tp_hit: 'TP hit',
    closed_externally: 'Closed manually',
    closed_externally_after_partial: 'Closed manually (after partial)',
    flipped_by_opposite_signal: 'Flipped by opposite signal',
  };

  // Exit reason — prefer the backend's real, verified exit_reason (written directly
  // onto the signal doc as of the Aug 1 fix); fall back to the old heuristic only
  // for legacy trades that closed before that fix existed.
  let exitReason = '—';
  if (signal.status === 'duplicate_skipped') {
    exitReason = 'Duplicate Skipped';
  } else if (signal.exit_reason) {
    exitReason = EXIT_REASON_LABELS[signal.exit_reason] || signal.exit_reason;
  } else if (closingFills.length > 0 || trailDetectedClose) {
    if (partialEvent && closedAfterPartial) {
      exitReason = 'Trailing stop';
    } else if (partialEvent) {
      exitReason = 'Trailing stop (partial close)';
    } else {
      // No exit_reason on record (legacy trade) — best-effort guess, labeled as such
      const slPrice = signal.sl_price ? parseFloat(signal.sl_price) : null;
      const tpPrice = signal.tp_price ? parseFloat(signal.tp_price) : null;
      const nearSl  = slPrice != null && exitPx != null && Math.abs(exitPx - slPrice) <= 50;
      const nearTp  = tpPrice != null && exitPx != null && Math.abs(exitPx - tpPrice) <= 50;
      exitReason = nearTp ? 'TP hit (est.)' : nearSl ? 'SL hit (est.)' : 'Closed externally (est.)';
    }
  } else if (fills.length === 0) {
    exitReason = signal.exit_mode === 'flip_only' ? 'Open — flip exit' : 'Position still open';
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="tdp">
        <div className="tdp-loading">
          <div className="spinner" />
          <span>Loading trade data…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tdp">

      {/* ── Key facts grid ─────────────────────────────────────────────── */}
      <div className="tdp-grid">
        {signal.status !== 'duplicate_skipped' && (
          <Cell label="Trade ID"     value={<span className="tdp-mono">{signal.tradeId || '—'}</span>} />
        )}
        <Cell label="Symbol"       value={coin} />
        <Cell label="Side"         value={
          <span className={`tdp-side tdp-side--${direction.toLowerCase()}`}>{direction}</span>
        } />
        <Cell label="Strategy"     value={signal.strategy || signal.source || '—'} />
        <Cell label="Leverage"     value={`${signal.leverage || 5}x ${signal.leverageType || 'Cross'}`} />
        <Cell label="Margin"       value={signal.size != null ? `$${(signal.size / (signal.leverage || 5)).toFixed(2)}` : '—'} />
        <Cell label="Position Size" value={signal.size != null ? `$${Number(signal.size).toFixed(2)}` : '—'} />
        <Cell label="Entry"        value={fmt(signal.fill_price || signal.price)} />
        <Cell label="Exit"         value={exitPx ? fmt(exitPx) : '—'} />
        <Cell label="Duration"     value={duration || (exitMs ? fmtDuration(Date.now() - signalMs) + ' (open)' : '—')} />
        <Cell label="Exit Reason"  value={exitReason} />
        <Cell label="Initial SL"   value={<span className="tdp-red">{fmt(signal.sl_price)}</span>} />
        <Cell label="Initial TP"   value={<span className="tdp-green">{fmt(signal.tp_price)}</span>} />
        <Cell
          label="Net PnL"
          value={
            signal.status === 'duplicate_skipped' ? '—' :
            estPnl != null
              ? <span style={{ color: pnlColor, fontWeight: 700 }}>
                  {estPnl === 0 ? '$0.0000' : `${estPnl > 0 ? '+' : '-'}$${Math.abs(estPnl).toFixed(4)}`}
                </span>
              : exitPx == null ? 'Position open' : '—'
          }
        />
        <Cell
          label="Fees Paid"
          value={totalFees > 0
            ? <span className="tdp-red">−${totalFees.toFixed(4)}</span>
            : '—'
          }
        />
      </div>

      {/* ── Partial close callout ───────────────────────────────────────── */}
      {partialEvent && (
        <div className="tdp-callout">
          ⚡ 50% partial close executed at Phase 4 · {partialEvent.note}
        </div>
      )}

      {/* ── Trailing events ────────────────────────────────────────────── */}
      <div className="tdp-section">
        <button
          className="tdp-trail-toggle"
          onClick={() => setTrailExpanded(x => !x)}
        >
          <span>Trailing Events</span>
          {trailEvents.length > 0 && (
            <span className="tdp-trail-count">{trailEvents.length}</span>
          )}
          <span className="tdp-trail-arrow">{trailExpanded ? '▲' : '▼'}</span>
        </button>

        {trailExpanded && (
          trailEvents.length === 0 ? (
            <div className="tdp-empty">
              No trailing events — trailing stop was not triggered for this trade.
            </div>
          ) : (
            <div className="tdp-trail-list">
              <div className="tdp-trail-header">
                <span>Time</span>
                <span>Phase</span>
                <span>Note</span>
                <span>SL change</span>
                <span>Price</span>
              </div>
              {trailEvents.map((e, i) => (
                <div key={e.id || i} className="tdp-trail-row">
                  <span className="tdp-trail-time">{fmtTime(e.timestamp)}</span>
                  <span className={`tdp-trail-phase tdp-trail-phase--${e.phase}`}>
                    Phase {e.phase}
                  </span>
                  <span className="tdp-trail-note">{
                    e.note === 'position_closed_externally'   ? 'SL triggered' :
                    e.note === 'position_closed_after_partial'? 'Position closed (after partial)' :
                    e.note || '—'
                  }</span>
                  <span className="tdp-trail-sl">
                    <span className="tdp-red">${(e.old_sl || 0).toFixed(2)}</span>
                    <span className="tdp-arrow"> → </span>
                    <span className="tdp-green">${(e.new_sl || 0).toFixed(2)}</span>
                  </span>
                  <span className="tdp-trail-price">{fmt(e.price)}</span>
                </div>
              ))}
            </div>
          )
        )}
      </div>

    </div>
  );
}

function Cell({ label, value }) {
  return (
    <div className="tdp-cell">
      <div className="tdp-label">{label}</div>
      <div className="tdp-value">{value}</div>
    </div>
  );
}
